import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  Check,
} from "lucide-react";
import { BASE_URL } from "../../config/axios";
import usePermission from "../../config/permissions";
import Logo from "../../assets/images/logo.png";
import "./Login.css";

/* ───────────────────────── Static content ───────────────────────── */

// The bid lifecycle. Each stage is a diya that gets lit in turn.
const STAGES = [
  {
    title: "Discover",
    text: "Track new RFPs and notices across infrastructure sectors, states and clients in one register.",
  },
  {
    title: "Evaluate",
    text: "Review eligibility, scope and competition, then record the bid or no-bid decision.",
  },
  {
    title: "Prepare",
    text: "Assign team leads, collect documents and CVs, and follow progress until the package is complete.",
  },
  {
    title: "Submit",
    text: "Watch submission dates and days remaining so nothing reaches the deadline unprepared.",
  },
];

// Sign-in process shown on screen after the form is submitted
const STEPS = [
  { label: "Verifying credentials", sub: "Checking your email and password" },
  { label: "Securing your session", sub: "Saving your sign-in details" },
  { label: "Loading your permissions", sub: "Applying your role and access rights" },
  { label: "Preparing your workspace", sub: "Opening the Business Development Portal" },
];

const SPARK_COLORS = ["#ffd36e", "#ff8a1f", "#ff4d9a", "#fff3b0", "#f7b32b"];
const BULB_COLORS = ["#ff5252", "#ffd36e", "#ff8a1f", "#ff4d9a", "#8be28b"];

// Firework rockets fired when sign-in completes
const BURSTS = [
  { x: 20, y: 26, d: 0 },
  { x: 78, y: 20, d: 0.15 },
  { x: 50, y: 40, d: 0.3 },
  { x: 88, y: 58, d: 0.45 },
  { x: 12, y: 62, d: 0.6 },
  { x: 62, y: 70, d: 0.75 },
];
const ROCKET_S = 0.55; // seconds a rocket takes to climb before it bursts
const PETAL_COLORS = ["#ff8a1f", "#f7b32b", "#ffb347", "#e8590c", "#c2185b"];

// How long the celebration plays before we move on to the dashboard.
// The gold curtain in Login.css starts at 2s and finishes at ~3s.
const CELEBRATION_MS = 3400;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Stable pseudo-random so decorations don't jump on re-render
const seeded = (i, n) => {
  const s = Math.sin(i * 97.13 + n * 31.7) * 43758.5453;
  return s - Math.floor(s);
};

const getErrorMessage = (err) => {
  const data = err?.response?.data;
  if (!data) return "Unable to reach the server. Check your connection and try again.";
  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data === "object") {
    const first = Object.entries(data)[0];
    if (first) {
      const [field, msg] = first;
      return `${field}: ${Array.isArray(msg) ? msg[0] : msg}`;
    }
  }
  return "Sign-in failed. Check your details and try again.";
};

/* ───────────────────────── Decorative pieces ───────────────────────── */

// Shared gradients, rendered once (not display:none, so references always resolve)
const SvgDefs = () => (
  <svg className="dv-defs" width="0" height="0" aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="dvGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffd36e" stopOpacity="0.85" />
        <stop offset="45%" stopColor="#ff8a1f" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#ff8a1f" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="dvFlame" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#ff5a1f" />
        <stop offset="55%" stopColor="#ff9d1f" />
        <stop offset="100%" stopColor="#ffe27a" />
      </linearGradient>
      <linearGradient id="dvBowl" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#e8751a" />
        <stop offset="60%" stopColor="#b8460f" />
        <stop offset="100%" stopColor="#7a2a08" />
      </linearGradient>
    </defs>
  </svg>
);

// A clay diya with a flame that flickers when lit
const Diya = ({ lit = true, size = 64, heat = 1, running = false }) => (
  <svg
    viewBox="0 0 100 100"
    width={size}
    height={size}
    className={`dv-diya ${lit ? "dv-diya--lit" : ""} ${running ? "dv-diya--run" : ""}`}
    style={{ "--heat": heat }}
    aria-hidden="true"
  >
    <circle className="dv-glow" cx="50" cy="40" r="46" fill="url(#dvGlow)" />
    <g className="dv-flame">
      <path d="M50 8 C65 27 69 42 50 58 C31 42 35 27 50 8Z" fill="url(#dvFlame)" />
      <path
        className="dv-flame-core"
        d="M50 31 C58 41 59 48 50 57 C41 48 42 41 50 31Z"
        fill="#fff3b0"
      />
    </g>
    <rect x="48.3" y="54" width="3.4" height="10" rx="1.7" fill="#3b2314" />
    <path
      d="M8 62 L92 62 C88 84 70 95 50 95 C30 95 12 84 8 62Z"
      fill="url(#dvBowl)"
    />
    <ellipse cx="50" cy="62" rx="42" ry="5.5" fill="#ffb347" opacity="0.55" />
    <path d="M8 62 Q50 72 92 62" stroke="#ffd36e" strokeWidth="2.4" fill="none" opacity="0.9" />
    <circle cx="32" cy="77" r="2.2" fill="#ffd36e" />
    <circle cx="50" cy="83" r="2.2" fill="#ffd36e" />
    <circle cx="68" cy="77" r="2.2" fill="#ffd36e" />
  </svg>
);

// Rotating rangoli mandala built from three counter-rotating layers
const Rangoli = () => (
  <div className="dv-rangoli" aria-hidden="true">
    <svg viewBox="-100 -100 200 200" className="dv-r dv-r1">
      <circle r="98" className="dv-r-ring" />
      {Array.from({ length: 24 }).map((_, i) => (
        <path
          key={i}
          d="M0 -95 Q9 -82 0 -68 Q-9 -82 0 -95Z"
          transform={`rotate(${i * 15})`}
          className={i % 2 ? "dv-pa" : "dv-pb"}
        />
      ))}
      {Array.from({ length: 24 }).map((_, i) => (
        <circle key={i} cx="0" cy="-99" r="1.4" transform={`rotate(${i * 15 + 7.5})`} className="dv-dot" />
      ))}
    </svg>
    <svg viewBox="-100 -100 200 200" className="dv-r dv-r2">
      <circle r="64" className="dv-r-ring dv-r-ring--dash" />
      {Array.from({ length: 12 }).map((_, i) => (
        <path
          key={i}
          d="M0 -62 Q16 -46 0 -26 Q-16 -46 0 -62Z"
          transform={`rotate(${i * 30})`}
          className={i % 2 ? "dv-pc" : "dv-pd"}
        />
      ))}
    </svg>
    <svg viewBox="-100 -100 200 200" className="dv-r dv-r3">
      {Array.from({ length: 16 }).map((_, i) => (
        <path
          key={i}
          d="M0 -32 L6 -18 L0 -8 L-6 -18Z"
          transform={`rotate(${i * 22.5})`}
          className="dv-pe"
        />
      ))}
      <circle r="6" className="dv-core" />
    </svg>
  </div>
);

// One firework: a rocket climbs, flashes, then bursts into two rings of sparks
const Burst = ({ x, y, d }) => (
  <div className="dv-burst" style={{ left: `${x}%`, top: `${y}%` }}>
    <span className="dv-rocket" style={{ animationDelay: `${d}s` }} />
    <span className="dv-flash" style={{ animationDelay: `${d + ROCKET_S}s` }} />
    {Array.from({ length: 30 }).map((_, i) => (
      <span
        key={i}
        className="dv-spark"
        style={{
          "--a": `${i * 12}deg`,
          "--d": `${i % 2 === 0 ? 112 : 66}px`,
          "--c": SPARK_COLORS[(i + Math.round(d * 10)) % SPARK_COLORS.length],
          animationDelay: `${d + ROCKET_S}s`,
        }}
      />
    ))}
  </div>
);

// Marigold petals drifting down the whole screen
const PetalRain = () => {
  const petals = useMemo(
    () =>
      Array.from({ length: 48 }).map((_, i) => ({
        left: seeded(i, 11) * 100,
        size: 8 + seeded(i, 12) * 10,
        dur: 4 + seeded(i, 13) * 4,
        delay: seeded(i, 14) * 2.2,
        sway: (seeded(i, 15) - 0.5) * 180,
        rot: (seeded(i, 16) > 0.5 ? 1 : -1) * (360 + seeded(i, 17) * 360),
        c: PETAL_COLORS[i % PETAL_COLORS.length],
      })),
    []
  );
  return (
    <div className="dv-petals" aria-hidden="true">
      {petals.map((p, i) => (
        <span
          key={i}
          className="dv-petal"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 1.3,
            background: p.c,
            animationDuration: `${p.dur}s`,
            animationDelay: `${p.delay}s`,
            "--sway": `${p.sway}px`,
            "--rot": `${p.rot}deg`,
          }}
        />
      ))}
    </div>
  );
};

// Glowing sky lanterns (akash kandil) floating up
const SkyLanterns = () => {
  const items = useMemo(
    () =>
      Array.from({ length: 9 }).map((_, i) => ({
        left: 5 + i * 11 + seeded(i, 20) * 4,
        size: 26 + seeded(i, 21) * 22,
        dur: 6 + seeded(i, 22) * 3,
        delay: seeded(i, 23) * 1.4,
        dx: (seeded(i, 24) - 0.5) * 90,
      })),
    []
  );
  return (
    <div className="dv-lanterns" aria-hidden="true">
      {items.map((l, i) => (
        <span
          key={i}
          className="dv-lantern"
          style={{
            left: `${l.left}%`,
            "--s": `${l.size}px`,
            "--dx": `${l.dx}px`,
            animationDuration: `${l.dur}s`,
            animationDelay: `${l.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

// Golden curtain that sweeps the screen just before the dashboard opens
const Wash = () => (
  <div className="dv-wash" aria-hidden="true">
    <span lang="hi">शुभ दीपावली</span>
    <small>Opening your workspace</small>
  </div>
);

// Strung fairy lights across the top of the sign-in side
const FairyLights = () => {
  const n = 16;
  return (
    <div className="dv-lights" aria-hidden="true">
      <svg viewBox="0 0 400 44" preserveAspectRatio="none" className="dv-wire">
        <path d="M0 4 Q200 84 400 4" />
      </svg>
      {Array.from({ length: n }).map((_, i) => {
        const t = (i + 0.5) / n;
        return (
          <span
            key={i}
            className="dv-bulb"
            style={{
              left: `${t * 100}%`,
              top: `${4 + 80 * t * (1 - t)}px`,
              "--c": BULB_COLORS[i % BULB_COLORS.length],
              animationDelay: `${(i * 0.23).toFixed(2)}s`,
            }}
          />
        );
      })}
    </div>
  );
};

/* ───────────────────────── Component ───────────────────────── */

const Login = () => {
  const navigate = useNavigate();
  const { updatePermissions } = usePermission();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [errMsg, setErrMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // "form" | "processing"
  const [phase, setPhase] = useState("form");
  const [step, setStep] = useState(0); // index of the running step; STEPS.length = all done

  // Left panel: which stage diya is lit up to
  const [activeStage, setActiveStage] = useState(0);

  // The hero diya starts unlit and is lit shortly after the page opens
  const [heroLit, setHeroLit] = useState(false);
  const [userName, setUserName] = useState("");
  const spotRef = useRef(null);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const embers = useMemo(
    () =>
      Array.from({ length: 30 }).map((_, i) => ({
        left: seeded(i, 1) * 100,
        size: 2 + seeded(i, 2) * 4,
        dur: 8 + seeded(i, 3) * 10,
        delay: -seeded(i, 4) * 16,
        dx: (seeded(i, 5) - 0.5) * 140,
      })),
    []
  );
  const stars = useMemo(
    () =>
      Array.from({ length: 44 }).map((_, i) => ({
        left: seeded(i, 6) * 100,
        top: seeded(i, 7) * 100,
        size: 1 + seeded(i, 8) * 2,
        dur: 2 + seeded(i, 9) * 3,
        delay: -seeded(i, 10) * 5,
      })),
    []
  );

  /* Browser autofill support */
  useEffect(() => {
    const t = setTimeout(() => {
      const e = emailRef.current?.value;
      const p = passwordRef.current?.value;
      if (e || p) setFormData({ email: e || "", password: p || "" });
    }, 300);
    return () => clearTimeout(t);
  }, []);

  /* Light the hero diya a moment after load */
  useEffect(() => {
    const t = setTimeout(() => setHeroLit(true), 900);
    return () => clearTimeout(t);
  }, []);

  /* Cycle the lifecycle diyas while idle */
  useEffect(() => {
    if (phase === "processing") return undefined;
    const id = setInterval(() => setActiveStage((s) => (s + 1) % STAGES.length), 3200);
    return () => clearInterval(id);
  }, [phase]);

  /* While signing in, the lifecycle diyas follow the real sign-in steps */
  useEffect(() => {
    if (phase === "processing") setActiveStage(Math.min(step, STAGES.length - 1));
  }, [phase, step]);

  /* ───── validation ───── */
  const validateForm = () => {
    const next = {};
    if (!formData.email.trim()) next.email = "Enter your work email.";
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim()))
      next.email = "Enter a valid email address, like name@company.com.";
    if (!formData.password) next.password = "Enter your password.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    if (errMsg) setErrMsg("");
  };

  /* ───── submit ───── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase === "processing") return;
    if (!validateForm()) return;

    setErrMsg("");
    setStep(0);
    setPhase("processing");

    try {
      // Step 1 – verify credentials
      const [res] = await Promise.all([
        axios.post(`${BASE_URL}/user/login/`, { ...formData, email: formData.email.trim() }),
        sleep(1100), // keep the step visible even on a fast network
      ]);

      if (res.status !== 200) throw new Error("Invalid Email Or Password");

      const {
        access,
        refresh,
        payload,
        employeecode,
        payload_a,
        letter,
        sitemanagement_sub_company,
        sitemanagement_role,
        sitemanagement_project,
        sitemanagement_emp_id,
        sitemanagement_emp_code,
        sitemanagement_emp_hrms_code,
      } = res.data;

      const firstName = (payload_a?.name || "").trim().split(/\s+/)[0];
      if (firstName) setUserName(firstName);

      // Step 2 – store session
      setStep(1);
      const sessionData = {
        access_token: access || "",
        refresh_token: refresh || "",
        role: payload?.role || "",
        email: payload?.email || "",
        emp_code: employeecode || "",
        department: payload_a?.department || "",
        rh: payload_a?.reporting_head || "",
        profilepic: payload_a?.profilepic || "",
        letter_type: letter || "",
        name: payload_a?.name || "",
        company: payload_a?.division_name || "",
        company_id: payload_a?.sub_company_id || "",
        sitemanagement_role: sitemanagement_role || "",
        sitemanagement_emp_id: sitemanagement_emp_id || "",
        sitemanagement_emp_code: sitemanagement_emp_code || "",
        sitemanagement_project: sitemanagement_project?.id || "",
        sitemanagement_project_short_name: sitemanagement_project?.project_short_name || "",
        sitemanagement_project_site_prcode: sitemanagement_project?.site_prcode || "",
        sitemanagement_project_sub_company: sitemanagement_sub_company || "",
        emp_code_hrms: sitemanagement_emp_hrms_code || "",
      };
      Object.entries(sessionData).forEach(([k, v]) => sessionStorage.setItem(k, v));
      await sleep(900);

      // Step 3 – permissions
      setStep(2);
      updatePermissions();
      await sleep(900);

      // Step 4 – workspace
      setStep(3);
      await sleep(1000);

      // All done: diyas are lit, fireworks fire
      setStep(STEPS.length);
      await sleep(CELEBRATION_MS); // fireworks, petals, lanterns, golden curtain

      navigate("/");
      window.location.reload(); // same behaviour as the previous login page
    } catch (err) {
      const message = getErrorMessage(err);
      setErrMsg(message);
      toast.error(message, { position: "top-center", theme: "colored" });
      setPhase("form");
      setStep(0);
    }
  };

  const percent = Math.round((Math.min(step, STEPS.length) / STEPS.length) * 100);
  const allDone = step >= STEPS.length;
  const processing = phase === "processing";

  // The hero diya burns brighter as the form gets filled in
  const fill = (formData.email.trim() ? 0.5 : 0) + (formData.password ? 0.5 : 0);
  // ...and burns even higher as each sign-in step completes
  const heroHeat = 0.82 + fill * 0.38 + (processing ? 0.1 + 0.08 * Math.min(step, STEPS.length) : 0);

  // Cursor-follow candlelight on the left side (written straight to the DOM: no re-renders)
  const onAsideMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    spotRef.current?.style.setProperty("--mx", `${e.clientX - r.left}px`);
    spotRef.current?.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  /* ───────────────────────── Render ───────────────────────── */
  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <SvgDefs />

      {/* Celebration on successful sign-in */}
      {allDone && (
        <>
          <div className="dv-fireworks" aria-hidden="true">
            {BURSTS.map((b, i) => (
              <Burst key={i} {...b} />
            ))}
          </div>
          <SkyLanterns />
          <PetalRain />
          <Wash />
        </>
      )}

      <div
        className={`dv-page ${processing ? "dv-page--busy" : ""} ${allDone ? "dv-page--done" : ""}`}
      >
        {/* ═════════ Left: festival night sky ═════════ */}
        <aside className="dv-aside" aria-label="About the portal" onMouseMove={onAsideMove}>
          <div className="dv-bloom" aria-hidden="true" />
          <div className="dv-sky" aria-hidden="true">
            {stars.map((s, i) => (
              <span
                key={i}
                className="dv-star"
                style={{
                  left: `${s.left}%`,
                  top: `${s.top}%`,
                  width: s.size,
                  height: s.size,
                  animationDuration: `${s.dur}s`,
                  animationDelay: `${s.delay}s`,
                }}
              />
            ))}
          </div>

          <Rangoli />

          <div className="dv-embers" aria-hidden="true">
            {embers.map((m, i) => (
              <span
                key={i}
                className="dv-ember"
                style={{
                  left: `${m.left}%`,
                  width: m.size,
                  height: m.size,
                  animationDuration: `${m.dur}s`,
                  animationDelay: `${m.delay}s`,
                  "--dx": `${m.dx}px`,
                }}
              />
            ))}
          </div>

          <div className="dv-spot" ref={spotRef} aria-hidden="true" />

          <div className="dv-aside-inner">
            <motion.header
              className="dv-brand"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/" className="dv-logo-link">
                <img src={Logo} alt="Company logo" className="dv-logo" />
              </Link>
              <span className="dv-brand-divider" />
              <span className="dv-brand-name">Business Development Portal</span>
            </motion.header>

            {/* The memorable moment: a diya that lights up the whole scene */}
            <div className="dv-hero">
              <motion.div
                className="dv-hero-diya"
                initial={{ opacity: 0, y: 30, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
              >
                <Diya size={150} lit={heroLit} heat={heroHeat} />
              </motion.div>

              <motion.h1
                className="dv-headline"
                lang="hi"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                शुभ दीपावली
              </motion.h1>
              <motion.p
                className="dv-lead"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                Happy Diwali. May every bid your team prepares this year close on time and
                come home a win.
              </motion.p>
            </div>

            {/* Bid lifecycle as a row of diyas */}
            <div className="dv-lifecycle">
              <div className="dv-diya-row" role="list">
                {STAGES.map((s, i) => (
                  <button
                    type="button"
                    key={s.title}
                    role="listitem"
                    className={`dv-stage ${i === activeStage ? "dv-stage--active" : ""}`}
                    onMouseEnter={() => setActiveStage(i)}
                    onFocus={() => setActiveStage(i)}
                    onClick={() => setActiveStage(i)}
                  >
                    <Diya size={54} lit={i <= activeStage} />
                    <span>{s.title}</span>
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={activeStage}
                  className="dv-stage-text"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {STAGES[activeStage].text}
                </motion.p>
              </AnimatePresence>
            </div>

            <footer className="dv-aside-foot">
              <ShieldCheck size={16} />
              <span>Access is role-based. Every sign-in is tied to your employee account.</span>
            </footer>
          </div>
        </aside>

        {/* ═════════ Right: sign-in ═════════ */}
        <main className="dv-main">
          <FairyLights />

          <div className="dv-main-inner">
            {/* compact festive banner shown only on small screens */}
            <div className="dv-mobile-brand">
              <Diya size={52} heat={0.9 + fill * 0.3} />
              <div>
                <strong lang="hi">शुभ दीपावली</strong>
                <span>Business Development Portal</span>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {!processing ? (
                <motion.section
                  key="form"
                  className="dv-panel dv-card"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45 }}
                >
                  <div className="dv-panel-head">
                    <h2>Sign in</h2>
                    <p>Use your work email to open your dashboard.</p>
                  </div>

                  <form onSubmit={handleSubmit} noValidate className="dv-form">
                    {/* Email */}
                    <div className="dv-field">
                      <label htmlFor="email">Work email</label>
                      <div className={`dv-input ${errors.email ? "dv-input--error" : ""}`}>
                        <Mail size={18} className="dv-input-icon" />
                        <input
                          ref={emailRef}
                          id="email"
                          name="email"
                          type="text"
                          inputMode="email"
                          autoComplete="email"
                          placeholder="name@company.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          aria-invalid={!!errors.email}
                          aria-describedby={errors.email ? "email-err" : undefined}
                        />
                      </div>
                      <AnimatePresence>
                        {errors.email && (
                          <motion.span
                            id="email-err"
                            className="dv-field-error"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            {errors.email}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Password */}
                    <div className="dv-field">
                      <div className="dv-label-row">
                        <label htmlFor="password">Password</label>
                        <button
                          type="button"
                          className="dv-link"
                          onClick={() => navigate("/forgotpassword")}
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className={`dv-input ${errors.password ? "dv-input--error" : ""}`}>
                        <Lock size={18} className="dv-input-icon" />
                        <input
                          ref={passwordRef}
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                          value={formData.password}
                          onChange={handleInputChange}
                          aria-invalid={!!errors.password}
                          aria-describedby={errors.password ? "password-err" : undefined}
                        />
                        <button
                          type="button"
                          className="dv-eye"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <AnimatePresence>
                        {errors.password && (
                          <motion.span
                            id="password-err"
                            className="dv-field-error"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            {errors.password}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>

                    <AnimatePresence>
                      {errMsg && (
                        <motion.div
                          className="dv-alert"
                          role="alert"
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0, x: [0, -6, 6, -4, 4, 0] }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.45 }}
                        >
                          <AlertCircle size={18} />
                          <div>
                            <strong>We couldn't sign you in</strong>
                            <span>{errMsg}</span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.button
                      type="submit"
                      className="dv-submit"
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.985 }}
                    >
                      <span>Sign in</span>
                      <ArrowRight size={18} className="dv-submit-arrow" />
                    </motion.button>
                  </form>

                  <p className="dv-help">
                    Don't have an account? Ask your administrator to set up your access.
                  </p>
                </motion.section>
              ) : (
                <motion.section
                  key="process"
                  className="dv-panel dv-card dv-process"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45 }}
                  aria-live="polite"
                >
                  {/* One diya per step: each one is lit as its step completes */}
                  <div className="dv-process-diyas">
                    {STEPS.map((s, i) => (
                      <Diya
                        key={s.label}
                        size={62}
                        lit={i < step}
                        running={i === step}
                      />
                    ))}
                  </div>

                  <h2 className="dv-process-title">
                    {allDone ? "You're signed in. Happy Diwali!" : "Signing you in"}
                  </h2>
                  {allDone && userName && (
                    <motion.p
                      className="dv-welcome"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      Welcome back, {userName}.
                    </motion.p>
                  )}
                  <p className="dv-process-user">
                    {formData.email.trim()}
                    <span className="dv-pct">{percent}%</span>
                  </p>

                  {/* Steps */}
                  <ol className="dv-steps">
                    {STEPS.map((s, i) => {
                      const state = i < step ? "done" : i === step ? "running" : "waiting";
                      return (
                        <motion.li
                          key={s.label}
                          className={`dv-step dv-step--${state}`}
                          initial={{ opacity: 0, x: -14 }}
                          animate={{ opacity: state === "waiting" ? 0.45 : 1, x: 0 }}
                          transition={{ duration: 0.4, delay: i * 0.08 }}
                        >
                          <span className="dv-step-mark">
                            {state === "done" ? (
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 400, damping: 14 }}
                              >
                                <Check size={14} strokeWidth={3} />
                              </motion.span>
                            ) : state === "running" ? (
                              <span className="dv-spinner" />
                            ) : (
                              <span className="dv-dot" />
                            )}
                          </span>
                          <span className="dv-step-text">
                            <strong>{s.label}</strong>
                            <em>{s.sub}</em>
                          </span>
                        </motion.li>
                      );
                    })}
                  </ol>

                  <div className="dv-bar" aria-hidden="true">
                    <motion.div
                      className="dv-bar-fill"
                      animate={{ width: `${percent}%` }}
                      transition={{ duration: 0.7, ease: "easeInOut" }}
                    />
                  </div>
                </motion.section>
              )}
            </AnimatePresence>

            <p className="dv-legal">
              <ShieldCheck size={14} />
              Authorised users only. Activity on this portal is logged.
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

export default Login;


// import React, { useState, useEffect, useRef } from "react";
// import axios from "axios";
// import { useNavigate, Link } from "react-router-dom";
// import { ToastContainer, toast } from "react-toastify";
// import { motion, AnimatePresence } from "framer-motion";
// import {
//   Eye,
//   EyeOff,
//   Lock,
//   Mail,
//   ArrowRight,
//   ShieldCheck,
//   AlertCircle,
//   Check,
//   FileSearch,
//   Scale,
//   FolderOpen,
//   Send,
// } from "lucide-react";
// import { BASE_URL } from "../../config/axios";
// import usePermission from "../../config/permissions";
// import Logo from "../../assets/images/logo.png";
// import "./Login.css";

// /* ───────────────────────── Static content ───────────────────────── */

// // Left panel: the bid lifecycle the portal manages
// const STAGES = [
//   {
//     icon: FileSearch,
//     title: "Discover tenders",
//     text: "Track new RFPs and notices across infrastructure sectors, states and clients in one register.",
//   },
//   {
//     icon: Scale,
//     title: "Evaluate the bid",
//     text: "Review eligibility, scope and competition, then record the bid or no-bid decision.",
//   },
//   {
//     icon: FolderOpen,
//     title: "Prepare the proposal",
//     text: "Assign team leads, collect documents and CVs, and follow progress until the package is complete.",
//   },
//   {
//     icon: Send,
//     title: "Submit on time",
//     text: "Watch submission dates and days remaining so nothing reaches the deadline unprepared.",
//   },
// ];

// // Sign-in process shown on screen after the form is submitted
// const STEPS = [
//   { label: "Verifying credentials", sub: "Checking your email and password" },
//   { label: "Securing your session", sub: "Saving your sign-in details" },
//   { label: "Loading your permissions", sub: "Applying your role and access rights" },
//   { label: "Preparing your workspace", sub: "Opening the Business Development Portal" },
// ];

// const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// const getErrorMessage = (err) => {
//   const data = err?.response?.data;
//   if (!data) return "Unable to reach the server. Check your connection and try again.";
//   if (typeof data.error === "string") return data.error;
//   if (typeof data.detail === "string") return data.detail;
//   if (typeof data === "object") {
//     const first = Object.entries(data)[0];
//     if (first) {
//       const [field, msg] = first;
//       return `${field}: ${Array.isArray(msg) ? msg[0] : msg}`;
//     }
//   }
//   return "Sign-in failed. Check your details and try again.";
// };

// /* ───────────────────────── Component ───────────────────────── */

// const Login = () => {
//   const navigate = useNavigate();
//   const { updatePermissions } = usePermission();

//   const [formData, setFormData] = useState({ email: "", password: "" });
//   const [errors, setErrors] = useState({});
//   const [errMsg, setErrMsg] = useState("");
//   const [showPassword, setShowPassword] = useState(false);

//   // "form" | "processing"
//   const [phase, setPhase] = useState("form");
//   const [step, setStep] = useState(0); // index of the running step; STEPS.length = all done

//   // Left panel: cycling lifecycle stage
//   const [activeStage, setActiveStage] = useState(0);

//   const emailRef = useRef(null);
//   const passwordRef = useRef(null);

//   /* Browser autofill support */
//   useEffect(() => {
//     const t = setTimeout(() => {
//       const e = emailRef.current?.value;
//       const p = passwordRef.current?.value;
//       if (e || p) setFormData({ email: e || "", password: p || "" });
//     }, 300);
//     return () => clearTimeout(t);
//   }, []);

//   /* Cycle the lifecycle showcase */
//   useEffect(() => {
//     const id = setInterval(() => setActiveStage((s) => (s + 1) % STAGES.length), 3200);
//     return () => clearInterval(id);
//   }, []);

//   /* ───── validation ───── */
//   const validateForm = () => {
//     const next = {};
//     if (!formData.email.trim()) next.email = "Enter your work email.";
//     else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim()))
//       next.email = "Enter a valid email address, like name@company.com.";
//     if (!formData.password) next.password = "Enter your password.";
//     setErrors(next);
//     return Object.keys(next).length === 0;
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((p) => ({ ...p, [name]: value }));
//     if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
//     if (errMsg) setErrMsg("");
//   };

//   /* ───── submit ───── */
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (phase === "processing") return;
//     if (!validateForm()) return;

//     setErrMsg("");
//     setStep(0);
//     setPhase("processing");

//     try {
//       // Step 1 – verify credentials
//       const [res] = await Promise.all([
//         axios.post(`${BASE_URL}/user/login/`, { ...formData, email: formData.email.trim() }),
//         sleep(1100), // keep the step visible even on a fast network
//       ]);

//       if (res.status !== 200) throw new Error("Invalid Email Or Password");

//       const {
//         access,
//         refresh,
//         payload,
//         employeecode,
//         payload_a,
//         letter,
//         sitemanagement_sub_company,
//         sitemanagement_role,
//         sitemanagement_project,
//         sitemanagement_emp_id,
//         sitemanagement_emp_code,
//         sitemanagement_emp_hrms_code,
//       } = res.data;

//       // Step 2 – store session
//       setStep(1);
//       const sessionData = {
//         access_token: access || "",
//         refresh_token: refresh || "",
//         role: payload?.role || "",
//         email: payload?.email || "",
//         emp_code: employeecode || "",
//         department: payload_a?.department || "",
//         rh: payload_a?.reporting_head || "",
//         profilepic: payload_a?.profilepic || "",
//         letter_type: letter || "",
//         name: payload_a?.name || "",
//         company: payload_a?.division_name || "",
//         company_id: payload_a?.sub_company_id || "",
//         sitemanagement_role: sitemanagement_role || "",
//         sitemanagement_emp_id: sitemanagement_emp_id || "",
//         sitemanagement_emp_code: sitemanagement_emp_code || "",
//         sitemanagement_project: sitemanagement_project?.id || "",
//         sitemanagement_project_short_name: sitemanagement_project?.project_short_name || "",
//         sitemanagement_project_site_prcode: sitemanagement_project?.site_prcode || "",
//         sitemanagement_project_sub_company: sitemanagement_sub_company || "",
//         emp_code_hrms: sitemanagement_emp_hrms_code || "",
//       };
//       Object.entries(sessionData).forEach(([k, v]) => sessionStorage.setItem(k, v));
//       await sleep(900);

//       // Step 3 – permissions
//       setStep(2);
//       updatePermissions();
//       await sleep(900);

//       // Step 4 – workspace
//       setStep(3);
//       await sleep(1000);

//       // All done
//       setStep(STEPS.length);
//       await sleep(700);

//       navigate("/");
//       window.location.reload(); // same behaviour as the previous login page
//     } catch (err) {
//       const message = getErrorMessage(err);
//       setErrMsg(message);
//       toast.error(message, { position: "top-center", theme: "colored" });
//       setPhase("form");
//       setStep(0);
//     }
//   };

//   const percent = Math.round((Math.min(step, STEPS.length) / STEPS.length) * 100);
//   const allDone = step >= STEPS.length;
//   const processing = phase === "processing";

//   /* ───────────────────────── Render ───────────────────────── */
//   return (
//     <>
//       <ToastContainer
//         position="top-center"
//         autoClose={3000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="colored"
//       />

//       <div className="lg-page">
//         {/* ═════════ Left: brand + bid lifecycle ═════════ */}
//         <aside className="lg-aside" aria-label="About the portal">
//           <div className="lg-aside-grid" aria-hidden="true" />
//           <div className="lg-aside-glow" aria-hidden="true" />

//           <div className="lg-aside-inner">
//             <motion.header
//               className="lg-brand"
//               initial={{ opacity: 0, y: -12 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.6 }}
//             >
//               <Link to="/" className="lg-logo-link">
//                 <img src={Logo} alt="Company logo" className="lg-logo" />
//               </Link>
//               <span className="lg-brand-divider" />
//               <span className="lg-brand-name">Business Development Portal</span>
//             </motion.header>

//             <motion.div
//               className="lg-hero"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.8, delay: 0.2 }}
//             >
//               <h1 className="lg-headline">
//                 Every bid, from tender notice to final submission.
//               </h1>
//               <p className="lg-lead">
//                 One register for your team to find opportunities, decide what to bid on, and
//                 deliver complete proposals before the deadline.
//               </p>
//             </motion.div>

//             {/* The memorable element: a live bid lifecycle */}
//             <div className="lg-lifecycle" role="list">
//               <div className="lg-track" aria-hidden="true">
//                 <motion.div
//                   className="lg-track-fill"
//                   animate={{ height: `${(activeStage / (STAGES.length - 1)) * 100}%` }}
//                   transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
//                 />
//               </div>

//               {STAGES.map((s, i) => {
//                 const Icon = s.icon;
//                 const state = i < activeStage ? "done" : i === activeStage ? "active" : "idle";
//                 return (
//                   <div
//                     key={s.title}
//                     role="listitem"
//                     className={`lg-stage lg-stage--${state}`}
//                     onMouseEnter={() => setActiveStage(i)}
//                   >
//                     <motion.span
//                       className="lg-stage-node"
//                       animate={{ scale: state === "active" ? 1.12 : 1 }}
//                       transition={{ type: "spring", stiffness: 300, damping: 18 }}
//                     >
//                       {state === "done" ? <Check size={16} strokeWidth={3} /> : <Icon size={16} />}
//                     </motion.span>
//                     <div className="lg-stage-body">
//                       <h3>{s.title}</h3>
//                       <AnimatePresence initial={false}>
//                         {state === "active" && (
//                           <motion.p
//                             key="t"
//                             initial={{ height: 0, opacity: 0 }}
//                             animate={{ height: "auto", opacity: 1 }}
//                             exit={{ height: 0, opacity: 0 }}
//                             transition={{ duration: 0.4 }}
//                           >
//                             {s.text}
//                           </motion.p>
//                         )}
//                       </AnimatePresence>
//                     </div>
//                   </div>
//                 );
//               })}
//             </div>

//             <footer className="lg-aside-foot">
//               <ShieldCheck size={16} />
//               <span>Access is role-based. Every sign-in is tied to your employee account.</span>
//             </footer>
//           </div>
//         </aside>

//         {/* ═════════ Right: sign-in ═════════ */}
//         <main className="lg-main">
//           <div className="lg-main-inner">
//             {/* compact brand shown only on small screens */}
//             <div className="lg-mobile-brand">
//               <img src={Logo} alt="Company logo" />
//               <span>Business Development Portal</span>
//             </div>

//             <AnimatePresence mode="wait">
//               {!processing ? (
//                 <motion.section
//                   key="form"
//                   className="lg-panel"
//                   initial={{ opacity: 0, y: 24 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -16 }}
//                   transition={{ duration: 0.45 }}
//                 >
//                   <div className="lg-panel-head">
//                     <h2>Sign in</h2>
//                     <p>Use your work email to open your dashboard.</p>
//                   </div>

//                   <form onSubmit={handleSubmit} noValidate className="lg-form">
//                     {/* Email */}
//                     <div className="lg-field">
//                       <label htmlFor="email">Work email</label>
//                       <div className={`lg-input ${errors.email ? "lg-input--error" : ""}`}>
//                         <Mail size={18} className="lg-input-icon" />
//                         <input
//                           ref={emailRef}
//                           id="email"
//                           name="email"
//                           type="text"
//                           inputMode="email"
//                           autoComplete="email"
//                           placeholder="name@company.com"
//                           value={formData.email}
//                           onChange={handleInputChange}
//                           aria-invalid={!!errors.email}
//                           aria-describedby={errors.email ? "email-err" : undefined}
//                         />
//                       </div>
//                       <AnimatePresence>
//                         {errors.email && (
//                           <motion.span
//                             id="email-err"
//                             className="lg-field-error"
//                             initial={{ opacity: 0, height: 0 }}
//                             animate={{ opacity: 1, height: "auto" }}
//                             exit={{ opacity: 0, height: 0 }}
//                           >
//                             {errors.email}
//                           </motion.span>
//                         )}
//                       </AnimatePresence>
//                     </div>

//                     {/* Password */}
//                     <div className="lg-field">
//                       <div className="lg-label-row">
//                         <label htmlFor="password">Password</label>
//                         <button
//                           type="button"
//                           className="lg-link"
//                           onClick={() => navigate("/forgotpassword")}
//                         >
//                           Forgot password?
//                         </button>
//                       </div>
//                       <div className={`lg-input ${errors.password ? "lg-input--error" : ""}`}>
//                         <Lock size={18} className="lg-input-icon" />
//                         <input
//                           ref={passwordRef}
//                           id="password"
//                           name="password"
//                           type={showPassword ? "text" : "password"}
//                           autoComplete="current-password"
//                           placeholder="Enter your password"
//                           value={formData.password}
//                           onChange={handleInputChange}
//                           aria-invalid={!!errors.password}
//                           aria-describedby={errors.password ? "password-err" : undefined}
//                         />
//                         <button
//                           type="button"
//                           className="lg-eye"
//                           onClick={() => setShowPassword((s) => !s)}
//                           aria-label={showPassword ? "Hide password" : "Show password"}
//                         >
//                           {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
//                         </button>
//                       </div>
//                       <AnimatePresence>
//                         {errors.password && (
//                           <motion.span
//                             id="password-err"
//                             className="lg-field-error"
//                             initial={{ opacity: 0, height: 0 }}
//                             animate={{ opacity: 1, height: "auto" }}
//                             exit={{ opacity: 0, height: 0 }}
//                           >
//                             {errors.password}
//                           </motion.span>
//                         )}
//                       </AnimatePresence>
//                     </div>

//                     <AnimatePresence>
//                       {errMsg && (
//                         <motion.div
//                           className="lg-alert"
//                           role="alert"
//                           initial={{ opacity: 0, y: -8 }}
//                           animate={{ opacity: 1, y: 0, x: [0, -6, 6, -4, 4, 0] }}
//                           exit={{ opacity: 0 }}
//                           transition={{ duration: 0.45 }}
//                         >
//                           <AlertCircle size={18} />
//                           <div>
//                             <strong>We couldn't sign you in</strong>
//                             <span>{errMsg}</span>
//                           </div>
//                         </motion.div>
//                       )}
//                     </AnimatePresence>

//                     <motion.button
//                       type="submit"
//                       className="lg-submit"
//                       whileHover={{ y: -1 }}
//                       whileTap={{ scale: 0.985 }}
//                     >
//                       <span>Sign in</span>
//                       <ArrowRight size={18} className="lg-submit-arrow" />
//                     </motion.button>
//                   </form>

//                   <p className="lg-help">
//                     Don't have an account? Ask your administrator to set up your access.
//                   </p>
//                 </motion.section>
//               ) : (
//                 <motion.section
//                   key="process"
//                   className="lg-panel lg-process"
//                   initial={{ opacity: 0, y: 24 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -16 }}
//                   transition={{ duration: 0.45 }}
//                   aria-live="polite"
//                 >
//                   {/* Progress ring */}
//                   <div className="lg-ring-wrap">
//                     <svg viewBox="0 0 120 120" className="lg-ring" aria-hidden="true">
//                       <circle cx="60" cy="60" r="52" className="lg-ring-bg" />
//                       <motion.circle
//                         cx="60"
//                         cy="60"
//                         r="52"
//                         className={`lg-ring-fg ${allDone ? "lg-ring-fg--done" : ""}`}
//                         strokeDasharray={2 * Math.PI * 52}
//                         animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - percent / 100) }}
//                         transition={{ duration: 0.7, ease: "easeInOut" }}
//                       />
//                     </svg>
//                     <div className="lg-ring-center">
//                       <AnimatePresence mode="wait">
//                         {allDone ? (
//                           <motion.span
//                             key="ok"
//                             className="lg-ring-check"
//                             initial={{ scale: 0, rotate: -45 }}
//                             animate={{ scale: 1, rotate: 0 }}
//                             transition={{ type: "spring", stiffness: 260, damping: 16 }}
//                           >
//                             <Check size={36} strokeWidth={3} />
//                           </motion.span>
//                         ) : (
//                           <motion.span
//                             key="pct"
//                             className="lg-ring-pct"
//                             initial={{ opacity: 0 }}
//                             animate={{ opacity: 1 }}
//                           >
//                             {percent}
//                             <small>%</small>
//                           </motion.span>
//                         )}
//                       </AnimatePresence>
//                     </div>
//                   </div>

//                   <h2 className="lg-process-title">
//                     {allDone ? "You're signed in" : "Signing you in"}
//                   </h2>
//                   <p className="lg-process-user">{formData.email.trim()}</p>

//                   {/* Steps */}
//                   <ol className="lg-steps">
//                     {STEPS.map((s, i) => {
//                       const state = i < step ? "done" : i === step ? "running" : "waiting";
//                       return (
//                         <motion.li
//                           key={s.label}
//                           className={`lg-step lg-step--${state}`}
//                           initial={{ opacity: 0, x: -14 }}
//                           animate={{ opacity: state === "waiting" ? 0.45 : 1, x: 0 }}
//                           transition={{ duration: 0.4, delay: i * 0.08 }}
//                         >
//                           <span className="lg-step-mark">
//                             {state === "done" ? (
//                               <motion.span
//                                 initial={{ scale: 0 }}
//                                 animate={{ scale: 1 }}
//                                 transition={{ type: "spring", stiffness: 400, damping: 14 }}
//                               >
//                                 <Check size={14} strokeWidth={3} />
//                               </motion.span>
//                             ) : state === "running" ? (
//                               <span className="lg-spinner" />
//                             ) : (
//                               <span className="lg-dot" />
//                             )}
//                           </span>
//                           <span className="lg-step-text">
//                             <strong>{s.label}</strong>
//                             <em>{s.sub}</em>
//                           </span>
//                         </motion.li>
//                       );
//                     })}
//                   </ol>

//                   <div className="lg-bar" aria-hidden="true">
//                     <motion.div
//                       className="lg-bar-fill"
//                       animate={{ width: `${percent}%` }}
//                       transition={{ duration: 0.7, ease: "easeInOut" }}
//                     />
//                   </div>
//                 </motion.section>
//               )}
//             </AnimatePresence>

//             <p className="lg-legal">
//               <ShieldCheck size={14} />
//               Authorised users only. Activity on this portal is logged.
//             </p>
//           </div>
//         </main>
//       </div>
//     </>
//   );
// };

// export default Login;